import { z } from "zod";
import { optional } from "./appointments";
import { recordId } from "./contracts";
import { customerContact, customerContactRequest } from "./customers";
import { searchTextUpTo, unset } from "./fields";
import { emailAddress, settingsName } from "./organization-settings";

export const SUBCONTRACT_PAGE_SIZE_MAX = 100;
export const SUBCONTRACTOR_SEARCH_MAX_LENGTH = 200;
export const SUBCONTRACTOR_SEARCH_RESULTS_MAX = 10;
export const SUBCONTRACT_SERVICES_MAX = 200;

const searchText = searchTextUpTo(SUBCONTRACTOR_SEARCH_MAX_LENGTH);

const trimmedSearch = z.preprocess((value) => (typeof value === "string" ? unset(value.trim()) : value), searchText.optional());

export const subcontractParty = z.object({
  id: recordId,
  name: z.string(),
  description: z.string().nullable(),
  email: z.string().nullable(),
  logo: z.string().nullable(),
  location: z.string().nullable(),
});
export type SubcontractParty = z.infer<typeof subcontractParty>;

export const subcontractService = z.object({ id: recordId, name: z.string() });
export type SubcontractService = z.infer<typeof subcontractService>;

export const subcontract = z.object({
  id: recordId,
  subcontractor: subcontractParty,
  vendor: z.object({ id: recordId, name: z.string() }),
  services: z.array(subcontractService),
  paymentTermsDays: z.number().int(),
  contact: customerContact.nullable(),
  createdAt: z.string(),
  endedAt: z.string().nullable(),
});
export type Subcontract = z.infer<typeof subcontract>;

export const subcontractReadQuery = z.object({ actingOrganization: optional(recordId.toLowerCase()) });
export type SubcontractReadQuery = z.infer<typeof subcontractReadQuery>;

export const subcontractListQuery = subcontractReadQuery.extend({
  q: trimmedSearch,
  service: trimmedSearch,
  status: z.preprocess(unset, z.enum(["live", "ended"]).default("live")),
  sort: z.preprocess(unset, z.enum(["name", "email"]).default("name")),
  order: z.preprocess(unset, z.enum(["asc", "desc"]).default("asc")),
  page: z.preprocess(unset, z.coerce.number().int().min(1).max(10_000).default(1)),
  pageSize: z.preprocess(unset, z.coerce.number().int().min(1).max(SUBCONTRACT_PAGE_SIZE_MAX).default(25)),
});
export type SubcontractListQuery = z.infer<typeof subcontractListQuery>;

export const subcontractPage = z.object({ items: z.array(subcontract), total: z.number().int().nonnegative() });
export type SubcontractPage = z.infer<typeof subcontractPage>;

export const subcontractorSearchQuery = subcontractReadQuery.extend({ q: trimmedSearch });
export type SubcontractorSearchQuery = z.infer<typeof subcontractorSearchQuery>;

export const reachedSubcontractor = z.object({
  id: recordId,
  name: z.string(),
  email: z.string().nullable(),
  logo: z.string().nullable(),
  linked: z.boolean(),
});
export type ReachedSubcontractor = z.infer<typeof reachedSubcontractor>;

export const subcontractorSearch = z.object({ items: z.array(reachedSubcontractor) });
export type SubcontractorSearch = z.infer<typeof subcontractorSearch>;

const actingOrganization = optional(recordId.toLowerCase());

export const subcontractLinkRequest = z.union([
  z.object({ actingOrganization, subcontractor: recordId.toLowerCase() }).strict(),
  z.object({ actingOrganization, email: emailAddress }).strict(),
]);
export type SubcontractLinkRequest = { actingOrganization?: string } & ({ subcontractor: string } | { email: string });

export const subcontractServicesRequest = z.object({ services: z.array(recordId.toLowerCase()).max(SUBCONTRACT_SERVICES_MAX) }).strict();
export type SubcontractServicesRequest = z.infer<typeof subcontractServicesRequest>;

export const subcontractContactRequest = customerContactRequest;
export type SubcontractContactRequest = { name: string; email: string; phone?: string | null; designation?: string | null };

export const workerConversionRequest = z.object({ companyName: settingsName, companyEmail: optional(emailAddress), alsoWorker: z.boolean() }).strict();
export type WorkerConversionRequest = { companyName: string; companyEmail?: string; alsoWorker: boolean };

export const openWork = z.object({ appointments: z.number().int().nonnegative(), workDays: z.number().int().nonnegative() });
export type OpenWork = z.infer<typeof openWork>;

export const workerConversion = z.object({ subcontract, workerContract: recordId.nullable(), openWork });
export type WorkerConversion = z.infer<typeof workerConversion>;
